/**
 * Belote bot.
 *
 * Works from the bot's own view only:
 *  - Bidding: scores the hand for a candidate trump suit (jack and nine are worth the most) and takes when the score
 *    clears a threshold set by the difficulty's aggression. Round one counts the turned-up card as ours.
 *  - Leading: a taker holding the master trumps draws them; otherwise cash master cards in side suits, else lead low
 *    in the suit most likely to reach the partner.
 *  - Following: feed points to a winning partner, win as cheaply as possible against opponents, and otherwise shed
 *    the cheapest card. HARD/EXPERT/NORMAL track played cards to recognise master cards.
 */
import { buildDeck, parseCard, type CardId, type Suit } from '../../core/cards.js';
import { chance, type BotContext, type BotEngine } from '../../bots/types.js';
import {
  belotePartnerOf,
  beloteCardPoints,
  beloteStrength,
  beloteTrickWinner,
  type BeloteAction,
  type BeloteLegal,
  type BeloteTrickPlay,
  type BeloteView,
} from './engine.js';

const suitOf = (id: CardId) => parseCard(id).suit;
const rankOf = (id: CardId) => parseCard(id).rank;

const TRUMP_VALUE: Record<number, number> = { 11: 24, 9: 18, 14: 12, 10: 10, 13: 5, 12: 4, 8: 2, 7: 2 };

/** How promising a hand is with `suit` as trump (higher = better). */
export function beloteHandStrength(cards: readonly CardId[], suit: Suit): number {
  let score = 0;
  let trumps = 0;
  for (const c of cards) {
    const p = parseCard(c);
    if (p.suit === suit) {
      score += TRUMP_VALUE[p.rank] ?? 0;
      trumps++;
    } else if (p.rank === 14) score += 8;
    else if (p.rank === 10 && cards.includes(`A${p.suit}`)) score += 3;
  }
  if (trumps > 2) score += (trumps - 2) * 4;
  if (cards.includes(`K${suit}`) && cards.includes(`Q${suit}`)) score += 6;
  return score;
}

function bidChoice(view: BeloteView, legal: Extract<BeloteLegal, { kind: 'bid' }>, ctx: BotContext): BeloteAction {
  const threshold = (legal.round === 1 ? 50 : 44) - (ctx.profile.aggression - 0.5) * 12;
  let best: { suit: Suit; score: number } | null = null;
  for (const suit of legal.suits) {
    const cards = legal.round === 1 && suit === suitOf(view.upCard) ? [...view.hand, view.upCard] : view.hand;
    const score = beloteHandStrength(cards, suit);
    if (!best || score > best.score) best = { suit, score };
  }
  if (best && best.score >= threshold) return { type: 'bid', suit: best.suit };
  return { type: 'pass' };
}

interface Knowledge {
  unseen: Set<CardId>;
}

function buildKnowledge(view: BeloteView, ctx: BotContext): Knowledge {
  const seen = new Set<CardId>(view.hand);
  view.trick.forEach((p) => seen.add(p.card));
  const remembered = ctx.profile.memory;
  view.tricks.forEach((t, i) => {
    // Forgetful bots only recall the most recent tricks.
    if (remembered >= 1 || i >= view.tricks.length - Math.max(1, Math.round(view.tricks.length * remembered))) {
      for (const p of t.plays) seen.add(p.card);
    }
  });
  return { unseen: new Set(buildDeck().filter((c) => rankOf(c) >= 7 && !seen.has(c))) };
}

function isMaster(card: CardId, k: Knowledge, trump: Suit): boolean {
  const s = suitOf(card);
  const mine = beloteStrength(card, trump);
  for (const u of k.unseen) if (suitOf(u) === s && beloteStrength(u, trump) > mine) return false;
  return true;
}

const cost = (c: CardId, trump: Suit) => beloteCardPoints(c, trump) * 10 + beloteStrength(c, trump) + (suitOf(c) === trump ? 100 : 0);
const cheapest = (cards: readonly CardId[], trump: Suit) => [...cards].sort((a, b) => cost(a, trump) - cost(b, trump))[0]!;
const richest = (cards: readonly CardId[], trump: Suit) =>
  [...cards].sort((a, b) => beloteCardPoints(b, trump) - beloteCardPoints(a, trump) || beloteStrength(a, trump) - beloteStrength(b, trump))[0]!;

function wouldWin(trick: readonly BeloteTrickPlay[], seat: number, card: CardId, trump: Suit): boolean {
  if (trick.length === 0) return true;
  return beloteTrickWinner([...trick, { seat, card }], trump) === seat;
}

function lead(view: BeloteView, legal: CardId[], k: Knowledge | null): CardId {
  const trump = view.trump!;
  const me = view.seat!;
  const trumps = legal.filter((c) => suitOf(c) === trump);
  const side = legal.filter((c) => suitOf(c) !== trump);
  const weTook = view.taker !== null && view.taker % 2 === me % 2;

  // Draw the opponents' trumps while we hold the master.
  if (weTook && trumps.length > 0) {
    const top = [...trumps].sort((a, b) => beloteStrength(b, trump) - beloteStrength(a, trump))[0]!;
    if (k ? isMaster(top, k, trump) : rankOf(top) === 11) return top;
  }
  // Cash a master card in a side suit.
  if (k) {
    const masters = side.filter((c) => isMaster(c, k, trump));
    if (masters.length) return richest(masters, trump);
  } else {
    const aces = side.filter((c) => rankOf(c) === 14);
    if (aces.length) return aces[0]!;
  }
  if (side.length) return cheapest(side, trump);
  return cheapest(trumps.length ? trumps : legal, trump);
}

function follow(view: BeloteView, legal: CardId[], k: Knowledge | null): CardId {
  const me = view.seat!;
  const trump = view.trump!;
  const trick = view.trick;
  const partner = belotePartnerOf(me);
  const winnerSeat = beloteTrickWinner(trick, trump);
  const winnerCard = trick.find((p) => p.seat === winnerSeat)!.card;
  const last = trick.length === 3;
  const winners = legal.filter((c) => wouldWin(trick, me, c, trump));

  if (winnerSeat === partner) {
    // Partner is winning: add points when they are safe, otherwise keep the strong cards.
    const safe = last || (k ? isMaster(winnerCard, k, trump) : rankOf(winnerCard) === 14 || suitOf(winnerCard) === trump);
    return safe ? richest(legal, trump) : cheapest(legal, trump);
  }
  if (winners.length > 0) {
    const onTable = trick.reduce((n, p) => n + beloteCardPoints(p.card, trump), 0);
    // Do not spend a trump on an empty trick unless it is the last card.
    const plain = winners.filter((c) => suitOf(c) !== trump || last || onTable >= 10);
    const pool = plain.length ? plain : winners;
    return cheapest(pool, trump);
  }
  return cheapest(legal, trump);
}

export function beloteBotChoose(view: BeloteView, legal: CardId[], ctx: BotContext): CardId {
  if (legal.length === 1) return legal[0]!;
  const k = ctx.profile.memory > 0 ? buildKnowledge(view, ctx) : null;
  return view.trick.length === 0 ? lead(view, legal, k) : follow(view, legal, k);
}

export const beloteBot: BotEngine<BeloteView, BeloteLegal, BeloteAction> = {
  decideAction(view, legal, ctx) {
    if (legal.kind === 'bid') return this.chooseBet!(view, legal, ctx);
    if (legal.kind === 'play') return this.chooseCard!(view, legal, ctx);
    throw new Error('Bot asked to act without legal actions');
  },

  chooseBet(view, legal, ctx) {
    if (legal.kind !== 'bid') return this.handleTimeout(view, legal, ctx);
    if (chance(ctx, ctx.profile.mistakeRate)) {
      const options: BeloteAction[] = [{ type: 'pass' }, ...legal.suits.map((suit) => ({ type: 'bid' as const, suit }))];
      return options[ctx.rng.int(options.length)]!;
    }
    return bidChoice(view, legal, ctx);
  },

  chooseCard(view, legal, ctx) {
    if (legal.kind !== 'play') return this.handleTimeout(view, legal, ctx);
    const cards = legal.cards;
    if (cards.length === 1) return { type: 'play', card: cards[0]! };
    if (chance(ctx, ctx.profile.mistakeRate)) return { type: 'play', card: cards[ctx.rng.int(cards.length)]! };
    const card = beloteBotChoose(view, cards, ctx);
    return { type: 'play', card: cards.includes(card) ? card : cards[0]! };
  },

  evaluateState(view) {
    const me = view.seat;
    if (me === null) return 0;
    const team = me % 2;
    const diff = view.teamScores[team]! - view.teamScores[1 - team]!;
    return Math.max(0, Math.min(1, 0.5 + diff / 600));
  },

  calculateRisk() {
    return 0;
  },

  handleTimeout(view, legal) {
    if (legal.kind === 'bid') return { type: 'pass' };
    if (legal.kind === 'play') return { type: 'play', card: cheapest(legal.cards, view.trump ?? 'S') };
    throw new Error('No legal action');
  },
};
